import { useApolloClient } from '@apollo/client'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import React, { FC, memo, useCallback, useEffect, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import Measure from '@Assets/SVG/Measure'
import { LoadingSpinner } from '@Components/Loading'
import { NativeModal, NativeModalBody, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { BlockTypeWithGlobal, Maybe } from '@Generated/graphql'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { getSinglePostByID } from '@Queries/cms/SinglePost.query'
import { HorizontalCarousel } from '@Shared/Components/Carousels/HorizontalCarousel/HorizontalCarousel'
import { TabButtons } from '@Shared/Components/Tabs/TabButtons'

import styles from './SizeGuide.module.scss'
import { SizeGuideItemProps, SizeGuideMap } from './SizeGuideMap'

type Variant = 'accordion' | 'accordion-side-pane'

interface ProductSizeGuideProps {
  asAccordion?: boolean
  blocks: Maybe<BlockTypeWithGlobal[]>
  children?: React.ReactNode
  className?: string
  id: string
  productType?: string
  showBorderBelow?: boolean
  skipScrollLock?: boolean
  title: string
  triggerClassName?: string
  variant?: Variant
  withIcon?: boolean
}

export const ProductSizeGuideV1: FC<ProductSizeGuideProps> = memo(({
  blocks,
  children,
  className,
  id,
  showBorderBelow,
  title,
  triggerClassName,
  withIcon,
}) => {
  const { t } = useTranslation()
  const actionRef = useNativeModalActions()
  const components = useBlockComponents(blocks || [], id)

  if (!title || components.length === 0) {
    return null
  }

  return (
    <div className={clsx(className, withIcon && styles.sizeGuide, showBorderBelow && styles.showBorderBelow)}>
      <button className={clsx(styles.ruleTrigger, triggerClassName)} id={`sizeguide-sidepanel_${id}`} onClick={() => actionRef.current?.open()} type="button">
        <Measure />
        {title}
      </button>
      <NativeModal actionRef={actionRef} alignment="right" className={styles.modalRight} onOpen={() => ampli.ppGuideInfoLinkOpen()}>
        <NativeModalHeader>
          {t('cms:SIZE_GUIDE_LABEL')}
        </NativeModalHeader>
        <NativeModalBody>
          <div className={styles.accordionContents}>
            {components}
            {children}
          </div>
        </NativeModalBody>
      </NativeModal>
    </div>
  )
})

export const ProductSizeGuideV2: FC<ProductSizeGuideProps> = memo(({
  blocks,
  children,
  id,
  productType,
  showBorderBelow,
  title,
  triggerClassName,
  withIcon,
}) => {
  const { t } = useTranslation()
  const tabs = SizeGuideMap[productType!]
  const actionRef = useNativeModalActions()
  const [tabBlocks, setTabBlocks] = useState(blocks)
  const components = useBlockComponents(tabBlocks || [], id)
  const [activeTab, setActiveTab] = useState<string | null>('measure')
  const [loading, setLoading] = useState(false)
  const client = useApolloClient()
  const router = useRouter()
  const changeContent = useCallback(async (tab: SizeGuideItemProps) => {
    setActiveTab(tab.key)
    setLoading(true)
    if (tab.pageType && tab.pageID) {
      const res = await getSinglePostByID(client, tab.pageType, tab.pageID)
      setTabBlocks((res?.data?.page?.blocks as (undefined | BlockTypeWithGlobal[])) || blocks)
    } else if (tab.useDefault) {
      setTabBlocks(blocks)
    } else {
      setTabBlocks([])
    }
    setLoading(false)
  }, [blocks, client])

  useEffect(() => {
    if ('sizeguide' in router.query && typeof document !== 'undefined') {
      const { query } = router
      const tab = tabs.find(ta => ta.key === query.sizeguide)
      if (tab) {
        changeContent(tab)
      }
      document.getElementById(`sizeguide-sidepanel_${id}`)
        ?.click()
      delete query.sizeguide
      // TODO: missing pathname in first and second argument.
      router.replace({ query }, undefined, {
        locale: router.locale,
        shallow: true,
        scroll: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!title || components.length === 0) {
    return null
  }

  return (
    <div className={clsx(withIcon && styles.sizeGuide, showBorderBelow && styles.showBorderBelow)}>
      <button className={clsx(styles.ruleTrigger, triggerClassName)} id={`sizeguide-sidepanel_${id}`} onClick={() => actionRef.current?.open()} type="button">
        <Measure />
        {title}
      </button>
      <NativeModal actionRef={actionRef} onOpen={() => ampli.ppGuideInfoLinkOpen()}>
        <NativeModalHeader>
          {t('cms:SIZE_GUIDE_LABEL')}
        </NativeModalHeader>
        <NativeModalBody>
          <div className={styles.accordionContents}>
            <HorizontalCarousel carouselClassName={styles.carousel} showProgressbar snap unevenChildWidth>
              <TabButtons
                activeKey={activeTab}
                className={styles.tabs}
                onClick={changeContent}
                tabs={
                      tabs
                    }
              />
            </HorizontalCarousel>
            {loading && <LoadingSpinner className={styles.spinner} />}
            {!loading && activeTab === 'chart' && children}
            {!loading && components}
          </div>
        </NativeModalBody>
      </NativeModal>
    </div>
  )
})

export const ProductSizeGuide: FC<ProductSizeGuideProps> = memo((props) => {
  const useSizeguideTabs = useSplitTest('size_guide_tabs_v1', 'b')
  return useSizeguideTabs && props.productType && SizeGuideMap[props.productType] ? <ProductSizeGuideV2 {...props} /> : <ProductSizeGuideV1 {...props} />
})
